/**
 * Fotos vor dem Hochladen verkleinern.
 *
 * Ein iPhone-Bild wiegt roh drei bis fünf Megabyte. An der Zapfsäule über
 * Mobilfunk ist das der Unterschied zwischen Warten und Weiterfahren, und
 * gelegentlich reisst es die 8-MB-Grenze des Workers. Gebraucht wird das Bild
 * nur, um eine Zahl später nachlesen zu können - 2000 px Kantenlänge reichen
 * dafür mit Abstand.
 *
 * Grundsatz: Diese Datei darf einen Upload niemals verhindern. Geht beim
 * Verkleinern irgendetwas schief - fehlendes createImageBitmap, verweigerte
 * Leinwand, unbekanntes Format -, wird das Original hochgeladen.
 */

const KANTE = 2000;
const GUETE = 0.82;

/** Unterhalb dieser Grösse lohnt das Umrechnen nicht. */
const SCHON_KLEIN = 600 * 1024;

export async function verkleinereBild(datei: File): Promise<File> {
  if (!datei.type.startsWith('image/') || datei.size <= SCHON_KLEIN) {
    return datei;
  }

  let bild: ImageBitmap | undefined;
  try {
    bild = await createImageBitmap(datei);

    const faktor = Math.min(1, KANTE / Math.max(bild.width, bild.height));
    const breite = Math.round(bild.width * faktor);
    const hoehe = Math.round(bild.height * faktor);

    const leinwand = document.createElement('canvas');
    leinwand.width = breite;
    leinwand.height = hoehe;

    const stift = leinwand.getContext('2d');
    if (!stift) return datei;
    stift.drawImage(bild, 0, 0, breite, hoehe);

    const blob = await new Promise<Blob | null>((fertig) => {
      leinwand.toBlob(fertig, 'image/jpeg', GUETE);
    });

    // Bei einem bereits gut gepackten Bild kann das Ergebnis grösser sein.
    if (!blob || blob.size >= datei.size) return datei;

    return new File([blob], name(datei), { type: 'image/jpeg' });
  } catch {
    return datei;
  } finally {
    bild?.close();
  }
}

/** Aus "beleg.heic" wird "beleg.jpg" - der Inhalt ist jetzt ein JPEG. */
function name(datei: File): string {
  const ohneEndung = datei.name.replace(/\.[^.]+$/, '');
  return `${ohneEndung || 'foto'}.jpg`;
}
